import { useState } from 'react';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import { DomainChipsInput, useDomainDraft } from '../components/DomainChipsInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { toast } from 'sonner';
import { useCreatePendingTenant, useUpdatePendingTenant } from '../api/hooks';
import type { PendingTenant } from '../api/types';

export function PendingDialog({
  initial,
  onClose,
}: {
  initial: PendingTenant | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const editor = useDomainDraft(initial?.allowed_email_domains ?? []);
  const create = useCreatePendingTenant();
  const update = useUpdatePendingTenant();
  const pending = create.isPending || update.isPending;

  const save = async () => {
    const allowed_email_domains = editor.prepareSave();
    if (allowed_email_domains === null) return;
    const payload = { name: name.trim(), slug: slug.trim() || null, allowed_email_domains };
    try {
      if (initial) {
        await update.mutateAsync({ id: initial.id, ...payload });
        toast.success('Pending tenant updated');
      } else {
        await create.mutateAsync(payload);
        toast.success('Pending tenant created');
      }
      onClose();
    } catch (e) {
      const data = (e as { response?: { data?: { error?: string; detail?: string } } })?.response
        ?.data;
      const msg = data?.error ?? data?.detail ?? (e instanceof Error ? e.message : 'Save failed');
      toast.error(msg);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit pending tenant' : 'Add tenant'}</DialogTitle>
          <DialogDescription>
            Pre-register an organization and its allowed email domains. After saving, use Prepare
            teams to set up the team before anyone signs up. Preparation sends no emails.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="slug">Slug (optional)</Label>
            <Input id="slug" value={slug} onChange={e => setSlug(e.target.value)} />
          </div>
          <DomainChipsInput editor={editor} disabled={pending} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={pending || !name.trim()}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
